import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash/fp';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import { change } from 'redux-form';
import styled, { css } from 'styled-components';

import useIsEditableWithOptions from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import {
  EDITOR_ROOT_PATH,
  INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID,
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import Tooltip from 'dashboard/components/Tooltip';
import Banner from 'dashboard/components-lib/Banner';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import NavBreadcrumb from 'dashboard/components-lib/NavBreadcrumb';
import { BreadcrumbProps } from 'dashboard/components-lib/NavBreadcrumb/NavBreadcrumb';
import { FullScreenHeaderContainer, StickyContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate, InquiryTemplateTier } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import { ProductFeatureName } from 'dashboard/models/organization';
import { openInquiryTemplateVersionPreview } from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import hoverableMixin from 'design-system/mixins/hoverable';
import { Variant } from 'design-system/theme';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useConfirmation from 'lib/hooks/useConfirmation';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { assertNever } from 'lib/utils/types';
import { getOnlyLastPath, removeTrailingSlash } from 'lib/utils/url';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: -${Spacing[2]};
  margin-right: ${Spacing[4]};
  overflow: hidden;
`;

const StickerWrapper = styled.div`
  display: flex;
  margin-right: ${Spacing[2]};
  margin-top: auto;
  margin-bottom: auto;
`;

const InternalToggleWrapper = styled.div`
  display: flex;
  margin-right: ${Spacing[2]};
  align-items: center;
`;

const ToggleLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  margin-left: ${Spacing[2]};
`;

const HeaderButton = styled(Button).attrs(() => ({ width: 'auto' }))``;
const WorkflowRunsButton = styled(Button).attrs(() => ({ width: 'auto' }))`
  ${hoverableMixin}
  ${({ theme }) => css`
    background-color: ${theme.color.variants.basic.default};
    color: ${theme.color.variants.basic.text};
  `}
`;

export interface PendingStates {
  isDiscarding: boolean;
  isLoading: boolean;
  isPublishing: boolean;
  isUpdating: boolean;
}

interface InquiryTemplateHeaderProps {
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  currentEnvironmentType: Environment['type'];
  lastPath: string;
  workflowName?: string;
  pendingStates: PendingStates;
  isFormPristine: boolean;
  isFormValid: boolean;
  hasValidationErrors: boolean;
  isLoadingWorkflowSchema: boolean;
  showStatusSticker: boolean;
  handleDiscardDraft: (inquiryTemplateId: string) => Promise<void>;
  handlePublishDraft: () => void;
  handleSave: () => void;
  handleSelectVersion: (versionId: string) => void;
  handleToggleVersionsPanel: () => void;
}

const InquiryTemplateHeader = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  currentEnvironmentType,
  lastPath,
  workflowName,
  pendingStates,
  isFormPristine,
  isFormValid,
  hasValidationErrors,
  isLoadingWorkflowSchema,
  showStatusSticker,
  handleDiscardDraft,
  handlePublishDraft,
  handleSave,
  handleSelectVersion,
  handleToggleVersionsPanel,
}: InquiryTemplateHeaderProps) => {
  const isPersonerd = useIsPersonerd();
  const isITEInternal = useIsITEInternal();
  const isInquiryInspectEnabled = useFeatureFlag('dashboard:fle-inquiry-inspect');
  const hasInquiriesEnhanced = useProductFeature(ProductFeatureName.InquiriesEnhanced);
  const displayEditingActions = useIsEditableWithOptions();

  const [internalEditingEnabled, setInternalEditing] = useLocalStorage(
    LocalStorageKey.DashboardITEInternalEditingEnabled,
    true,
  );

  // allows Persona users (@withpersona account or impersonating) to clearly see when
  // internal features are enabled in the ITE header, as well as toggle that capability
  // without needing to configure a backend feature flag.
  const toggleInternalEditing = useCallback(() => {
    setInternalEditing(!internalEditingEnabled);
    location.reload();
  }, [internalEditingEnabled, setInternalEditing]);

  const [
    showDiscardDraftModal,
    { prompt: prompt, confirm: confirmDiscardDraft, cancel: cancelDiscardDraft },
  ] = useConfirmation();
  const { isDiscarding, isPublishing, isUpdating } = pendingStates;
  const {
    id: inquiryTemplateId,
    attributes: {
      name: inquiryTemplateName,
      'pricing-tier': pricingTier,
      'archived-at': archivedAt,
    },
    relationships: {
      'latest-draft-version': latestDraftVersion,
      'latest-published-version': latestPublishedVersion,
    },
  } = inquiryTemplate;
  const {
    id: versionId,
    attributes: { status: versionStatus },
  } = inquiryTemplateVersion;

  // The version id to use in the path; is "draft" placeholder value when the showing
  // a draft not yet initially
  const pathInquiryTemplateVersionId =
    inquiryTemplateVersion.id ?? INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID;

  const matchScreensPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/screens`,
    strict: false,
  });
  const matchFlowPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/flow`,
    strict: false,
  });
  const matchActionsPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/actions`,
    strict: false,
  });
  const matchFieldsPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/config/fields`,
    strict: false,
  });
  const matchVerificationsPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/config/verifications`,
    strict: false,
  });
  const matchThemePath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/theme`,
    strict: false,
  });
  const matchWorkflowRunsPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/workflow-runs`,
    strict: false,
  });
  const matchReviewChangesPath = useRouteMatch({
    path: `${EDITOR_ROOT_PATH}/review-changes`,
    strict: false,
  });

  const getLastBreadcrumbDisplayName = () => {
    if (matchActionsPath) {
      return workflowName ? `Actions: ${workflowName}` : 'Actions';
    } else if (matchScreensPath) {
      return 'Screens';
    } else if (matchFlowPath) {
      return 'Flow';
    } else if (matchFieldsPath) {
      return 'Fields';
    } else if (matchVerificationsPath) {
      return 'Verifications';
    } else if (matchWorkflowRunsPath) {
      return 'Workflow Runs';
    } else if (matchReviewChangesPath) {
      return 'Review Changes';
    } else {
      // capitalize last path for config editor and subsections
      return capitalize(lastPath);
    }
  };

  const baseNavPath = `/dashboard/inquiry-templates/${inquiryTemplateId}/${pathInquiryTemplateVersionId}`;
  const routesWithSubRoutesMatch = useRouteMatch({
    path: [`${EDITOR_ROOT_PATH}/config/`, `${EDITOR_ROOT_PATH}/theme/`],
    strict: true,
  });

  const breadcrumbs: BreadcrumbProps[] = [];

  if (!matchFlowPath) {
    // not exactly the flow path
    breadcrumbs.push({
      to: `${baseNavPath}/flow`,
      displayName: 'Flow',
    });
  }

  if (routesWithSubRoutesMatch) {
    breadcrumbs.push({
      // get the base route within the subroute string
      to: removeTrailingSlash(routesWithSubRoutesMatch.url),
      displayName: capitalize(removeTrailingSlash(getOnlyLastPath(routesWithSubRoutesMatch.url))),
    });
  }

  breadcrumbs.push({
    // last crumb is always disabled
    displayName: getLastBreadcrumbDisplayName(),
  });

  const navigate = useNavigate();

  const onBackClick = useCallback(() => {
    if (routesWithSubRoutesMatch) {
      navigate(removeTrailingSlash(routesWithSubRoutesMatch.url));
    } else {
      // If we're at the flow view, go back to templates view
      navigate(matchFlowPath ? '/dashboard/inquiry-templates' : `${baseNavPath}/flow`);
    }
  }, [navigate, matchFlowPath, routesWithSubRoutesMatch, baseNavPath]);

  const dispatch = useDispatch();

  // disable header buttons whenever ITE is in any pending state
  const processing = Object.values(pendingStates).some((pendingState) => !!pendingState);
  const omitHeaderControls = matchThemePath || matchWorkflowRunsPath;

  return (
    <>
      <ModalWrapper isOpen={showDiscardDraftModal} handleCloseModal={cancelDiscardDraft}>
        <ConfirmationModal
          cancelAction={cancelDiscardDraft}
          confirmAction={confirmDiscardDraft}
          header='Discard draft?'
          confirmActionText='Discard'
        >
          All unpublished changes in this draft will be discarded. This action is permanent and
          cannot be undone.
        </ConfirmationModal>
      </ModalWrapper>

      <StickyContainer data-test='inquiry-template-header'>
        {versionId != null && (
          <StatusBanner
            inquiryTemplateVersionId={versionId}
            inquiryTemplateName={inquiryTemplateName}
            latestPublishedVersionId={latestPublishedVersion.data?.id}
            latestDraftVersionId={latestDraftVersion.data?.id}
            status={versionStatus}
            handleSelectVersion={handleSelectVersion}
            archivedAt={archivedAt}
          />
        )}

        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBreadcrumb
              name={inquiryTemplateName}
              breadcrumbItems={breadcrumbs}
              hidden={breadcrumbs.length === 0}
              onBackClick={onBackClick}
              onNameUpdate={(name: string) => dispatch(change(form, 'template-name', name))}
              editable={displayEditingActions}
            />
            {showStatusSticker && (
              <StickerWrapper>
                <StatusSticker status={versionStatus} />
              </StickerWrapper>
            )}
            {hasInquiriesEnhanced && pricingTier === InquiryTemplateTier.Enhanced && (
              <StickerWrapper>
                <InquiriesGatingSticker
                  dataMarketingIdSuffix={`header-${versionStatus}`}
                  stickerType={EnhancedGatingStickerType.Enhanced}
                />
              </StickerWrapper>
            )}
            {/* Toggle should only be shown to Personerd user, which is NOT affected by this toggle */}
            {isPersonerd && (
              <InternalToggleWrapper>
                <ToggleSlider value={isITEInternal} onChange={toggleInternalEditing} />
                <ToggleLabel>Internal features</ToggleLabel>
              </InternalToggleWrapper>
            )}
          </HorizontalContainer>

          <HStack spacing={Spacing[3]}>
            <Permissioned permission={Permission.InquiryTemplateWrite} fallback={null}>
              {displayEditingActions && (
                <>
                  {matchReviewChangesPath &&
                    versionStatus === InquiryTemplateVersionStatus.Draft && (
                      <HeaderButton
                        id='templates-view__return-to-draft-button'
                        onClick={onBackClick}
                        variant='basic'
                      >
                        Back to Draft
                      </HeaderButton>
                    )}
                  {/** Display publish in all ITE screens except for theme */}
                  {!omitHeaderControls && versionStatus === InquiryTemplateVersionStatus.Draft && (
                    <Tooltip
                      placement='bottom'
                      tooltip={
                        <Paragraph style={{ maxWidth: '300px' }}>
                          Publishing is disabled because the template has errors (listed in the
                          bottom panel).
                        </Paragraph>
                      }
                      // Hide this tooltip if there are no errors, or if there are changes (meaning user has to save before publishing)
                      disabled={!isFormPristine || !hasValidationErrors}
                    >
                      <HeaderButton
                        id='templates-view__publish-draft'
                        data-test='templates-view__publish-draft'
                        aria-label='Publish'
                        disabled={
                          processing ||
                          isLoadingWorkflowSchema ||
                          !isFormPristine ||
                          hasValidationErrors
                        }
                        isPending={isPublishing}
                        onClick={handlePublishDraft}
                      >
                        Publish
                      </HeaderButton>
                    </Tooltip>
                  )}
                  {!matchReviewChangesPath && !omitHeaderControls && (
                    <HeaderButton
                      id='templates-view__save-template'
                      aria-label='Save'
                      disabled={processing || isFormPristine || !isFormValid}
                      isPending={isUpdating}
                      onClick={handleSave}
                    >
                      Save
                    </HeaderButton>
                  )}
                  {/** only display discard when in flow editor 'main hub' to avoid ambiguity RE global/context-local control (particularly in workflow editor)
                   Also, don't allow discarding if no published version exists */}
                  {matchFlowPath &&
                    versionStatus === InquiryTemplateVersionStatus.Draft &&
                    latestPublishedVersion.data?.id != null && (
                      <HeaderButton
                        id='templates-view__discard-draft'
                        variant='basic'
                        aria-label='Discard'
                        disabled={processing}
                        isPending={isDiscarding}
                        onClick={() => prompt(() => handleDiscardDraft(inquiryTemplateId))}
                      >
                        Discard
                      </HeaderButton>
                    )}
                </>
              )}
            </Permissioned>

            {archivedAt == null && (
              <Permissioned permission={Permission.InquiryWrite} fallback={null}>
                {!matchReviewChangesPath && (
                  <Tooltip
                    placement='bottom'
                    tooltip={
                      <Paragraph style={{ maxWidth: '300px' }}>
                        Preview runs in Sandbox environments. You can switch environments using the
                        menu at the top left of the dashboard.
                      </Paragraph>
                    }
                    disabled={currentEnvironmentType === ApiModel.EnvironmentSandbox}
                  >
                    <HeaderButton
                      id='preview__preview--button'
                      variant='basic'
                      aria-label='Preview'
                      onClick={() => {
                        SegmentClient.sendSegmentTrackEvent('Flow Editor - Select Preview', {
                          'inquiry-template': inquiryTemplateId,
                        });
                        openInquiryTemplateVersionPreview({
                          inquiryTemplateId,
                          inquiryTemplateVersionId: inquiryTemplateVersion.id,
                        });
                      }}
                      disabled={
                        processing ||
                        !isFormPristine ||
                        currentEnvironmentType !== ApiModel.EnvironmentSandbox ||
                        hasValidationErrors
                      }
                    >
                      Preview
                    </HeaderButton>
                  </Tooltip>
                )}
              </Permissioned>
            )}

            {archivedAt == null && isITEInternal && isInquiryInspectEnabled && (
              <Permissioned permission={Permission.InquiryWrite} fallback={null}>
                {!matchReviewChangesPath && (
                  <Tooltip
                    placement='bottom'
                    disabled={versionStatus === InquiryTemplateVersionStatus.Published}
                    tooltip='Inspect is available only for published versions.'
                  >
                    <HeaderButton
                      id='inspect__inspect--button'
                      variant='basic'
                      aria-label='Inspect'
                      onClick={() => {
                        window.open(`${baseNavPath}/inspect`, '_blank');
                      }}
                      disabled={
                        versionStatus === InquiryTemplateVersionStatus.Draft ||
                        processing ||
                        !isFormPristine
                      }
                    >
                      Inspect
                    </HeaderButton>
                  </Tooltip>
                )}
              </Permissioned>
            )}

            {matchFlowPath && (
              <HeaderButton
                id='templates-view__versions'
                variant='basic'
                aria-label='Versions'
                onClick={handleToggleVersionsPanel}
                disabled={processing}
              >
                Versions
              </HeaderButton>
            )}
            {isITEInternal && !matchReviewChangesPath && !matchWorkflowRunsPath && (
              <WorkflowRunsButton
                id='templates-view__workflow-runs'
                variant='basic'
                aria-label='Workflow Runs'
                to={`${baseNavPath}/workflow-runs`}
                disabled={processing}
              >
                Workflow Runs
              </WorkflowRunsButton>
            )}
            {matchFlowPath && (
              <FeatureFlagged featureFlag='global:enable-platform-connection-dashboard'>
                <DropdownMenu
                  trigger={
                    <Button aria-label='More actions' type='button' variant='inverse' width='icon'>
                      <MoreIcon />
                    </Button>
                  }
                >
                  <DropdownMenu.Item
                    as={Link}
                    to={`/dashboard/platform-connections/${inquiryTemplateVersion.id}`}
                  >
                    Connections
                  </DropdownMenu.Item>
                </DropdownMenu>
              </FeatureFlagged>
            )}
          </HStack>
        </FullScreenHeaderContainer>
      </StickyContainer>
    </>
  );
};

const VersionLinkButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.primary.brand600};
`;

interface StatusBannerProps {
  inquiryTemplateVersionId: string;
  inquiryTemplateName: string;
  latestPublishedVersionId?: string | undefined;
  latestDraftVersionId?: string | undefined;
  status: InquiryTemplateVersionStatus;
  handleSelectVersion: (versionId: string) => void;
  archivedAt: string | undefined;
}

export const StatusBanner = ({
  inquiryTemplateVersionId,
  inquiryTemplateName,
  latestPublishedVersionId,
  latestDraftVersionId,
  status,
  handleSelectVersion,
  archivedAt,
}: StatusBannerProps) => {
  if (archivedAt) {
    return (
      <Banner>
        <strong>{inquiryTemplateName}</strong> was archived on{' '}
        <strong>{formatDate(archivedAt)}</strong> and can no longer be modified.
      </Banner>
    );
  }
  switch (status) {
    case InquiryTemplateVersionStatus.Published:
      if (inquiryTemplateVersionId === latestPublishedVersionId) {
        return latestDraftVersionId ? (
          <Banner>
            A draft exists for <strong>{inquiryTemplateName}</strong>.
            <>
              {' This version is no longer editable - '}
              <VersionLinkButton
                id='inquiry-template-header__latest-draft-button'
                onClick={() => handleSelectVersion(latestDraftVersionId)}
              >
                view the current draft version.
              </VersionLinkButton>
            </>
          </Banner>
        ) : null;
      } else {
        const activeVersionId =
          latestDraftVersionId != null ? latestDraftVersionId : latestPublishedVersionId;
        const buttonText =
          latestDraftVersionId != null ? 'view the current draft.' : 'view the active version.';
        return (
          <Banner>
            You are viewing a previously published version for{' '}
            <strong>{inquiryTemplateName}</strong>.
            <>
              {' This version is no longer editable - '}
              <VersionLinkButton
                id='inquiry-template-header__active-version-button'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onClick={() => handleSelectVersion(activeVersionId)}
              >
                {buttonText}
              </VersionLinkButton>
            </>
          </Banner>
        );
      }
    default:
      return <></>;
  }
};

const StatusSticker = ({ status }: { status: InquiryTemplateVersionStatus }) => {
  const getStickerStatus = (): [Exclude<Variant, 'inverse'>, string] => {
    switch (status) {
      case InquiryTemplateVersionStatus.Published: {
        return ['success', 'Published'];
      }
      case InquiryTemplateVersionStatus.Draft: {
        return ['primary', 'Draft'];
      }
      default:
        assertNever(status);
    }
  };
  const [variantType, statusMessage] = getStickerStatus();
  return <Sticker variant={variantType}>{statusMessage}</Sticker>;
};

export default InquiryTemplateHeader;
